import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import {
  AlertTriangle, CalendarCheck, Check, CheckCircle2, ChevronRight, Clock, ExternalLink,
  ChevronDown, PhoneCall, PhoneIncoming, PhoneMissed, PhoneOutgoing, RefreshCw, RotateCcw, Search, UserRound, X as XIcon,
} from "lucide-react"
import { api } from "@/lib/api"
import { cn } from "@/lib/utils"
import { toast } from "@/components/portal/Toast"
import { Loading, ErrorBox } from "@/components/portal/LoadingError"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import { rueckrufErledigt } from "@/components/portal/RueckrufStatus"
import {
  CallDetailModal, UebernehmenKnopf, terminLink,
  fmtDuration, isDataCheckFlag, type Call, type TelefonData,
} from "@/pages/KiTelefonPage"

/**
 * KI Telefon (seit 02.11.2026; die alte Tabelle liegt unter /ki-telefon-alt).
 *
 * Paul: „Aktuell sieht es aus wie 1990". Die alte Seite ist eine Tabelle mit
 * neun Spalten und drei Status-Begriffen (Status, Flag, Erledigt). Hier gibt
 * es pro Anruf genau EINEN Zustand und eine Arbeitsliste „Zu erledigen", die
 * leer werden kann. Daten und Schreib-Endpunkte sind dieselben wie auf der
 * alten Seite — nur die Darstellung ist neu.
 */

type Ton = "rot" | "gelb" | "blau" | "gruen" | "grau"
type Zustand = { key: string; label: string; ton: Ton; zuTun: boolean; rang: number }

const TON: Record<Ton, { pill: string; punkt: string }> = {
  rot: { pill: "bg-destructive/10 text-destructive", punkt: "bg-destructive" },
  gelb: { pill: "bg-amber-500/10 text-amber-700 dark:text-amber-400", punkt: "bg-amber-500" },
  blau: { pill: "bg-primary/10 text-primary", punkt: "bg-primary" },
  gruen: { pill: "bg-success/10 text-success", punkt: "bg-success" },
  grau: { pill: "bg-muted text-muted-foreground", punkt: "bg-muted-foreground/40" },
}

/** Ein Zustand je Anruf — ersetzt Status-, Flag- und Erledigt-Spalte. */
export function zustandVon(c: Call): Zustand {
  if (c.status === "in_progress") return { key: "laeuft", label: "Läuft gerade", ton: "blau", zuTun: true, rang: -1 }
  if (c.erledigt_at) {
    const auto = (c.erledigt_von || "").startsWith("automatisch")
    return { key: "erledigt", label: auto ? "Automatisch erledigt" : `Erledigt${c.erledigt_von ? ` · ${c.erledigt_von}` : ""}`, ton: "grau", zuTun: false, rang: 99 }
  }
  const flagOffen = !!c.flag_severity && !c.flag_acknowledged_at
  if (flagOffen) {
    const u = c.flag_callback_urgency
    const dringlich = u === "now" ? 0 : u === "today" ? 1 : 2
    switch (c.flag_severity) {
      case "high":
        return { key: "rueckruf", label: u === "now" ? "Sofort zurückrufen" : u === "today" ? "Heute zurückrufen" : "Zurückrufen", ton: "rot", zuTun: true, rang: dringlich }
      case "silence":
        return { key: "stumm", label: "Bot verstummt – prüfen", ton: "rot", zuTun: true, rang: 0.5 }
      case "medium": {
        const vomKunden = isDataCheckFlag(c.flag_reason) && !!c.kunden_bestaetigung_status && c.kunden_bestaetigung_status !== "pending"
        if (vomKunden) return { key: "kunde", label: "Vom Kunden bestätigt", ton: "gelb", zuTun: true, rang: 4 }
        return isDataCheckFlag(c.flag_reason)
          ? { key: "daten", label: "Daten prüfen", ton: "gelb", zuTun: true, rang: 3 }
          : { key: "unklar", label: "Anliegen unklar", ton: "gelb", zuTun: true, rang: 3 }
      }
      default:
        return { key: "sync", label: "HERO-Übertragung fehlgeschlagen", ton: "rot", zuTun: true, rang: 2 }
    }
  }
  if (c.derived_callback && !rueckrufErledigt(c.rueckruf)) return { key: "evtl", label: "Evtl. Rückruf", ton: "gelb", zuTun: true, rang: 5 }
  if (c.termin_bestaetigung?.noetig) return { key: "termin", label: "Termin prüfen", ton: "gelb", zuTun: true, rang: 4 }
  if (c.status === "offen") return { key: "ohne", label: "Ohne Ergebnis", ton: "gelb", zuTun: true, rang: 6 }
  if (c.status === "failed") return { key: "fehler", label: "Technischer Fehler", ton: "rot", zuTun: true, rang: 2 }
  if (c.status === "cancelled") return { key: "aufgelegt", label: "Aufgelegt", ton: "grau", zuTun: false, rang: 99 }
  return { key: "bot", label: "Vom Assistenten erledigt", ton: "gruen", zuTun: false, rang: 99 }
}

const ANLIEGEN: Record<string, string> = {
  booking: "Termin", reschedule: "Verschiebung", cancel: "Absage", info: "Info",
  question: "Frage", complaint: "Beschwerde", emergency: "Notfall", other: "Sonstiges",
}

/** 491529999924424 → 0152 9999924424 (so liest das Büro Nummern). */
export function nummerLesbar(n?: string | null): string {
  const d = (n || "").replace(/\D/g, "")
  if (!d) return "Unbekannte Nummer"
  const nat = d.startsWith("49") ? "0" + d.slice(2) : d.startsWith("0") ? d : d
  const vorwahl = /^01[5-7]/.test(nat) ? 4 : nat.startsWith("0") ? 4 : 0
  return vorwahl ? `${nat.slice(0, vorwahl)} ${nat.slice(vorwahl)}` : nat
}

function initialen(name?: string | null): string {
  const t = (name || "").trim().split(/\s+/).filter(Boolean)
  if (!t.length) return ""
  return (t[0][0] + (t.length > 1 ? t[t.length - 1][0] : "")).toUpperCase()
}

function uhrzeit(iso?: string | null) {
  if (!iso) return ""
  return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
}

function tagLabel(iso?: string | null): string {
  if (!iso) return "Ohne Datum"
  const d = new Date(iso)
  const heute = new Date(); heute.setHours(0, 0, 0, 0)
  const tag = new Date(d); tag.setHours(0, 0, 0, 0)
  const diff = Math.round((heute.getTime() - tag.getTime()) / 86_400_000)
  if (diff === 0) return "Heute"
  if (diff === 1) return "Gestern"
  return d.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" })
}

type Ansicht = "zutun" | "bot" | "alle"

export default function KiTelefonNeuPage() {
  const qc = useQueryClient()
  const [tenant, setTenant] = useTenantFilter()
  const [ansicht, setAnsicht] = useState<Ansicht>("zutun")
  const [suche, setSuche] = useState("")
  const [offenId, setOffenId] = useState<string | null>(null)

  const { data, isLoading, error, refetch, isFetching } = useQuery<TelefonData>({
    queryKey: ["ki-telefon-data", tenant],
    queryFn: async () => {
      const url = `/admin/ki-telefon/api/data${tenant ? `?tenant=${encodeURIComponent(tenant)}` : ""}`
      const res = await fetch(url, { credentials: "include" })
      if (!res.ok) {
        const j = await res.json().catch(() => ({}))
        throw new Error(j.error || `HTTP ${res.status}`)
      }
      return res.json()
    },
    refetchInterval: (q) => (q.state.data?.calls?.some((c) => c.status === "in_progress") ? 5_000 : 30_000),
  })

  const { data: tenants } = useQuery({
    queryKey: ["tenants-dropdown"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/tenants", {})
      if (error) throw new Error(String(error))
      return data
    },
    staleTime: 1000 * 60 * 5,
  })
  const firmenName = useMemo(() => {
    const m = new Map<string, string>()
    tenants?.items.forEach((t) => m.set(t.slug, t.name))
    return (c: Call) => (c.portal_tenant_slug && m.get(c.portal_tenant_slug)) || c.portal_tenant_slug || c.tenant_slug
  }, [tenants])

  // Direktlinks: /ki-telefon?caller=<nummer> (Live-Banner, Anrufe-Liste,
  // „offener Anruf"-Hinweis) öffnet den NEUESTEN Anruf dieser Nummer —
  // Ziffern-Endvergleich, 0176… und +49176… sind egal. /ki-telefon/<call_id>
  // (Push, alte Admin-Links) öffnet genau diesen Anruf.
  const [searchParams, setSearchParams] = useSearchParams()
  const { callId: callIdParam } = useParams()
  const navigate = useNavigate()
  useEffect(() => {
    const calls = data?.calls
    if (!calls?.length) return
    if (callIdParam) {
      if (calls.some((c) => c.call_id === callIdParam)) { setOffenId(callIdParam); setAnsicht("alle") }
      navigate("/ki-telefon", { replace: true })
      return
    }
    const wanted = (searchParams.get("caller") || "").replace(/\D/g, "")
    if (!wanted) return
    const tail = wanted.slice(-9)
    const match = [...calls]
      .sort((a, b) => (b.started_at || "").localeCompare(a.started_at || ""))
      .find((c) => (c.caller_number || "").replace(/\D/g, "").endsWith(tail))
    if (match) { setOffenId(match.call_id); setAnsicht("alle") }
    setSearchParams({}, { replace: true })
  }, [searchParams, callIdParam, data?.calls, setSearchParams, navigate])

  // Esc schließt das Panel
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") setOffenId(null) }
    window.addEventListener("keydown", h)
    return () => window.removeEventListener("keydown", h)
  }, [])

  const alle = data?.calls || []
  const mitZustand = useMemo(() => alle.map((c) => ({ c, z: zustandVon(c) })), [alle])

  const kennzahlen = useMemo(() => {
    const echte = mitZustand.filter(({ c }) => !c.testanruf)
    const heute = echte.filter(({ c }) => tagLabel(c.started_at) === "Heute").length
    const termine = echte.filter(({ c }) => c.termin_bestaetigung || (c.hero_anlagen || []).some((a) => /termin/i.test(a))).length
    const ohneMensch = echte.filter(({ c, z }) => z.key === "bot" && !c.flag_severity).length
    const quote = echte.length ? Math.round((ohneMensch / echte.length) * 100) : 0
    return { heute, termine, quote, gesamt: echte.length, zuTun: mitZustand.filter(({ z }) => z.zuTun).length }
  }, [mitZustand])

  const sichtbar = useMemo(() => {
    const q = suche.trim().toLowerCase()
    let liste = mitZustand.filter(({ z }) => ansicht === "alle" || (ansicht === "zutun" ? z.zuTun : !z.zuTun))
    if (q) {
      const qd = q.replace(/\D/g, "")
      liste = liste.filter(({ c }) =>
        (c.contact_name || "").toLowerCase().includes(q)
        || (c.ai_headline || "").toLowerCase().includes(q)
        || (qd.length >= 3 && (c.caller_number || "").replace(/\D/g, "").includes(qd.replace(/^0/, ""))))
    }
    const ts = (c: Call) => (c.started_at ? new Date(c.started_at).getTime() : 0)
    // Paul 02.11.: immer chronologisch, neueste oben — auch in „Zu erledigen".
    // Die Dringlichkeit zeigt der Zustand (Farbe + Text), nicht die Reihenfolge.
    return [...liste].sort((a, b) => ts(b.c) - ts(a.c))
  }, [mitZustand, ansicht, suche])

  // In allen Reitern nach Tagen gruppiert („Heute", „Gestern", „Montag, 26. Oktober").
  const gruppen = useMemo(() => {
    const out: { titel: string; eintraege: typeof sichtbar }[] = []
    for (const e of sichtbar) {
      const t = tagLabel(e.c.started_at)
      if (!out.length || out[out.length - 1].titel !== t) out.push({ titel: t, eintraege: [] })
      out[out.length - 1].eintraege.push(e)
    }
    return out
  }, [sichtbar])

  const erledigen = async (c: Call) => {
    try {
      const r = await fetch(`/admin/ki-telefon/api/call/${encodeURIComponent(c.call_id)}/erledigt`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ erledigt: true }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
      if (d.schon && d.hinweis) toast(`Anruf ${d.hinweis}`, "warn")
      else toast("Erledigt ✓", "success")
      qc.invalidateQueries({ queryKey: ["ki-telefon-data"] })
    } catch (e) {
      toast("Fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error")
    }
  }

  const offen = offenId ? alle.find((c) => c.call_id === offenId) : null

  if (error) return <div className="p-10"><ErrorBox error={error} /></div>

  return (
    <div className="px-4 sm:px-10 py-8 max-w-[1100px]">
      {/* Kopf */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-[26px] font-semibold tracking-tight">KI Telefon</h1>
          <p className="text-[14px] text-muted-foreground mt-0.5">
            Was der Telefonassistent angenommen hat – und was davon noch bei euch liegt.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={tenant}
            onChange={(e) => setTenant(e.target.value)}
            className="h-9 rounded-lg border bg-[var(--bg-card)] px-3 text-[13px]"
            aria-label="Betrieb wählen"
          >
            <option value="">Alle Betriebe</option>
            {tenants?.items.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
          </select>
          <button
            onClick={() => refetch()}
            className="h-9 w-9 inline-flex items-center justify-center rounded-lg border bg-[var(--bg-card)] hover:bg-secondary"
            aria-label="Aktualisieren" title="Aktualisieren"
          >
            <RefreshCw className={cn("w-4 h-4", isFetching && "animate-spin")} />
          </button>
        </div>
      </div>

      {isLoading || !data ? <Loading label="Lädt Anrufe..." /> : (
        <>
          {/* Kennzahlen */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <Kennzahl
              label="Bei euch offen" wert={kennzahlen.zuTun} betont={kennzahlen.zuTun > 0}
              hinweis={kennzahlen.zuTun ? "braucht einen Menschen" : "alles abgearbeitet"}
              onClick={() => setAnsicht("zutun")}
            />
            <Kennzahl label="Anrufe heute" wert={kennzahlen.heute} hinweis={`${kennzahlen.gesamt} in der Liste`} />
            <Kennzahl label="Termine gebucht" wert={kennzahlen.termine} hinweis="vom Assistenten" icon={<CalendarCheck className="w-4 h-4" />} />
            <Kennzahl
              label="Ohne Menschen erledigt" wert={`${kennzahlen.quote} %`}
              hinweis="kein Rückruf, keine Prüfung" balken={kennzahlen.quote}
            />
          </div>

          {/* Ansicht + Suche */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex sm:inline-flex w-full sm:w-auto p-1 rounded-xl bg-muted" role="tablist">
              {([
                ["zutun", "Zu erledigen", kennzahlen.zuTun],
                ["bot", "Erledigt", alle.length - kennzahlen.zuTun],
                ["alle", "Alle", alle.length],
              ] as const).map(([k, label, n]) => (
                <button
                  key={k} role="tab" aria-selected={ansicht === k}
                  onClick={() => setAnsicht(k)}
                  className={cn(
                    "flex-1 sm:flex-none justify-center px-2 sm:px-3.5 py-1.5 rounded-lg text-[13px] font-medium whitespace-nowrap transition-colors inline-flex items-center gap-1.5 sm:gap-2",
                    ansicht === k ? "bg-[var(--bg-card)] shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {label}
                  <span className={cn(
                    "text-[11px] rounded-full px-1.5 min-w-[20px] text-center",
                    ansicht === k && k === "zutun" && n > 0 ? "bg-primary text-primary-foreground" : "bg-background/70",
                  )}>{n}</span>
                </button>
              ))}
            </div>
            <div className="relative ml-auto w-full sm:w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={suche} onChange={(e) => setSuche(e.target.value)}
                placeholder="Name, Nummer oder Thema"
                style={{ paddingLeft: 36 }}
                className="w-full h-9 rounded-lg border bg-[var(--bg-card)] pr-3 text-[13px]"
              />
            </div>
          </div>

          {/* Liste */}
          {sichtbar.length === 0 ? (
            <LeerZustand ansicht={ansicht} suche={!!suche} />
          ) : (
            <div className="space-y-6">
              {gruppen.map((g) => (
                <section key={g.titel || "zutun"}>
                  {g.titel && <h3 className="text-[15px] font-semibold text-foreground/80 mb-2 px-1">{g.titel}</h3>}
                  <div className="rounded-xl border bg-[var(--bg-card)] divide-y overflow-hidden">
                    {g.eintraege.map(({ c, z }) => (
                      <AnrufZeile
                        key={c.call_id} c={c} z={z} firma={firmenName(c)}
                        aktiv={offenId === c.call_id}
                        onOeffnen={() => setOffenId(c.call_id)}
                        onErledigen={() => erledigen(c)}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}

          <p className="mt-6 text-[12px] text-muted-foreground">
            Ansage, Stimme und Begrüßung stellt ihr im{" "}
            <a href={`${data.voice_agent_url}/admin/`} target="_blank" rel="noopener" className="text-primary hover:underline inline-flex items-center gap-0.5">
              Voice-Agent-Portal <ExternalLink className="w-3 h-3" />
            </a>{" "}ein · <a href="/ki-telefon-alt" className="text-primary hover:underline">alte Ansicht</a>
          </p>
        </>
      )}

      {offen && (
        <AnrufPanel c={offen} z={zustandVon(offen)} firma={firmenName(offen)}
          onClose={() => setOffenId(null)} onChange={() => refetch()} />
      )}
    </div>
  )
}

function Kennzahl({ label, wert, hinweis, betont, balken, icon, onClick }: {
  label: string; wert: number | string; hinweis?: string; betont?: boolean
  balken?: number; icon?: React.ReactNode; onClick?: () => void
}) {
  const El = onClick ? "button" : "div"
  return (
    <El
      onClick={onClick}
      className={cn(
        "text-left rounded-xl border bg-[var(--bg-card)] p-4 transition-colors",
        betont && "border-primary/40 bg-primary/[0.04]",
        onClick && "hover:border-primary/60",
      )}
    >
      <div className="flex items-center justify-between text-[12.5px] text-muted-foreground">
        {label}{icon}
      </div>
      <div className={cn("text-[28px] font-semibold tracking-tight mt-1 tabular-nums", betont && "text-primary")}>{wert}</div>
      {balken !== undefined && (
        <div className="h-1.5 rounded-full bg-muted mt-1 mb-1.5 overflow-hidden">
          <div className="h-full rounded-full bg-success" style={{ width: `${balken}%` }} />
        </div>
      )}
      {hinweis && <div className="text-[12px] text-muted-foreground">{hinweis}</div>}
    </El>
  )
}

function Avatar({ c, z }: { c: Call; z: Zustand }) {
  const ini = initialen(c.contact_name)
  const Icon = z.key === "laeuft" ? PhoneCall : z.ton === "rot" ? PhoneMissed : PhoneIncoming
  return (
    <div className="relative shrink-0">
      <div className={cn(
        "w-10 h-10 rounded-full flex items-center justify-center text-[13px] font-semibold",
        ini ? "bg-secondary text-foreground" : "bg-muted text-muted-foreground",
      )}>
        {ini || <Icon className="w-4 h-4" />}
      </div>
      {z.zuTun && (
        <span className={cn("absolute -right-0.5 -top-0.5 w-3 h-3 rounded-full ring-2 ring-[var(--bg-card)]", TON[z.ton].punkt,
          z.key === "laeuft" && "animate-pulse")} />
      )}
    </div>
  )
}

function ZustandPill({ z }: { z: Zustand }) {
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[12px] font-medium whitespace-nowrap", TON[z.ton].pill)}>
      {z.ton === "rot" && <AlertTriangle className="w-3 h-3" />}
      {z.ton === "gruen" && <CheckCircle2 className="w-3 h-3" />}
      {z.label}
    </span>
  )
}

function AnrufZeile({ c, z, firma, aktiv, onOeffnen, onErledigen }: {
  c: Call; z: Zustand; firma: string; aktiv: boolean
  onOeffnen: () => void; onErledigen: () => void
}) {
  const heroAnzahl = c.hero_anlagen?.length ?? 0
  const anliegen = c.intent ? (ANLIEGEN[c.intent] || c.intent) : null
  // Der Tag steht als Überschrift über der Gruppe, in der Zeile nur die Uhrzeit.
  const wann = c.started_at ? `${uhrzeit(c.started_at)} Uhr` : ""
  return (
    <div
      onClick={onOeffnen}
      className={cn(
        "group flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3.5 cursor-pointer transition-colors",
        aktiv ? "bg-primary/[0.06]" : "hover:bg-secondary/50",
      )}
    >
      <div className="hidden sm:block"><Avatar c={c} z={z} /></div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="font-semibold text-[14px] truncate">
            {c.testanruf && <span className="mr-1.5 text-[10.5px] font-semibold uppercase rounded bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200 px-1.5 py-0.5 align-middle">Test</span>}
            {c.contact_name || nummerLesbar(c.caller_number)}
          </span>
          {c.contact_name && <span className="text-[12.5px] text-muted-foreground truncate hidden sm:inline">{nummerLesbar(c.caller_number)}</span>}
        </div>
        <div className="text-[13.5px] text-foreground/80 line-clamp-2 sm:truncate mt-0.5">
          {c.ai_headline || <span className="italic text-muted-foreground">Keine Zusammenfassung</span>}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[12px] text-muted-foreground">
          <span className="md:hidden"><ZustandPill z={z} /></span>
          {/* Paul 02.11.: Datum/Uhrzeit größer — danach sucht man die Liste ab */}
          <span className="text-[13.5px] font-semibold text-foreground/80 tabular-nums">{wann}</span>
          {c.duration_seconds ? <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDuration(c.duration_seconds)}</span> : null}
          {anliegen && <span>{anliegen}</span>}
          <span className="truncate max-w-[180px]">{firma}</span>
          {heroAnzahl > 0 && (
            <span className="inline-flex items-center gap-1 text-success" title={c.hero_anlagen!.join(" · ")}>
              <Check className="w-3 h-3" />{heroAnzahl}× in HERO
            </span>
          )}
          {c.zugewiesen_an && <span className="inline-flex items-center gap-1">→ {c.zugewiesen_an}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <div className="hidden md:block"><ZustandPill z={z} /></div>
        {z.zuTun && z.key !== "laeuft" ? (
          <button
            onClick={(e) => { e.stopPropagation(); onErledigen() }}
            className="h-8 px-3 rounded-lg border bg-[var(--bg-card)] text-[12.5px] font-medium inline-flex items-center gap-1.5 hover:bg-success hover:text-white hover:border-success transition-colors"
            title="Nacharbeit erledigt"
          >
            <Check className="w-3.5 h-3.5" /><span className="hidden sm:inline">Erledigt</span>
          </button>
        ) : z.key === "laeuft" ? (
          <span onClick={(e) => e.stopPropagation()}><UebernehmenKnopf call={c} onChange={() => {}} /></span>
        ) : null}
        <ChevronRight className="hidden sm:block w-4 h-4 text-muted-foreground/60 group-hover:text-foreground" />
      </div>
    </div>
  )
}

function LeerZustand({ ansicht, suche }: { ansicht: Ansicht; suche: boolean }) {
  if (suche) return <div className="rounded-xl border border-dashed p-10 text-center text-muted-foreground text-[14px]">Kein Anruf passt zur Suche.</div>
  if (ansicht === "zutun") {
    return (
      <div className="rounded-xl border bg-[var(--bg-card)] p-12 text-center">
        <div className="w-12 h-12 rounded-full bg-success/10 text-success inline-flex items-center justify-center mb-3">
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <div className="text-[16px] font-semibold">Alles erledigt</div>
        <div className="text-[13.5px] text-muted-foreground mt-1">Kein Anruf wartet auf euch. Der Assistent hat den Rest übernommen.</div>
      </div>
    )
  }
  return <div className="rounded-xl border border-dashed p-10 text-center text-muted-foreground text-[14px]">Noch keine Gespräche.</div>
}

/** Seitenpanel rechts: oben wer/was, dann Nacharbeit, dann der bisherige Detailinhalt. */
function AnrufPanel({ c, z, firma, onClose, onChange }: {
  c: Call; z: Zustand; firma: string; onClose: () => void; onChange: () => void
}) {
  return (
    <>
      {/* Unter den Dialogen bleiben (Interne Aufgabe = 200, ModalShell-Standard),
          die aus dem Panel heraus geöffnet werden und per Portal am <body> hängen. */}
      <div className="fixed inset-0 z-[149] bg-black/20" onClick={onClose} />
      <aside
        className="fixed right-0 top-0 bottom-0 z-[150] w-full sm:w-[620px] bg-[var(--bg-card)] border-l shadow-2xl flex flex-col"
        data-kontext-popup="Anruf-Details" data-kontext-anruf-id={c.call_id}
      >
        <div className="px-6 pt-5 pb-4 border-b">
          <div className="flex items-start gap-3">
            <Avatar c={c} z={z} />
            <div className="min-w-0 flex-1">
              <div className="text-[17px] font-semibold truncate">{c.contact_name || nummerLesbar(c.caller_number)}</div>
              <div className="text-[13.5px] text-muted-foreground mt-0.5">
                {[c.contact_name ? nummerLesbar(c.caller_number) : null, firma,
                  `${tagLabel(c.started_at)}, ${uhrzeit(c.started_at)}`, fmtDuration(c.duration_seconds)]
                  .filter(Boolean).join(" · ")}
              </div>
              <div className="mt-2"><ZustandPill z={z} /></div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary" aria-label="Schließen">
              <XIcon className="w-4 h-4" />
            </button>
          </div>
          <NacharbeitLeiste c={c} onChange={onChange} />
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <CallDetailModal callId={c.call_id} onClose={onClose} eingebettet
            nacharbeitErledigt={!!c.erledigt_at} zugewiesenAn={c.zugewiesen_an} />
        </div>
      </aside>
    </>
  )
}

/**
 * Nacharbeit als Aktionsleiste unter dem Kopf des Panels — ersetzt die
 * Spalte „Erledigt" (Haken + Auswahl + Link). Dieselben Endpunkte wie
 * `NacharbeitZelle` und `TerminBestaetigung` auf /ki-telefon-alt.
 */
function NacharbeitLeiste({ c, onChange }: { c: Call; onChange: () => void }) {
  const qc = useQueryClient()
  const [busy, setBusy] = useState<string | null>(null)
  const erledigt = !!c.erledigt_at
  const termin = c.termin_bestaetigung

  const mitarbeiter = useQuery<{ mitarbeiter: { username: string; rolle: string }[] }>({
    queryKey: ["ki-telefon-mitarbeiter"],
    queryFn: async () => {
      const r = await fetch("/admin/ki-telefon/api/mitarbeiter", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    staleTime: 1000 * 60 * 10,
  })
  const namen = (mitarbeiter.data?.mitarbeiter || []).map((m) => m.username)
  if (c.zugewiesen_an && !namen.includes(c.zugewiesen_an)) namen.unshift(c.zugewiesen_an)

  const senden = async (pfad: string, body: unknown) => {
    setBusy(pfad)
    try {
      const r = await fetch(`/admin/ki-telefon/api/call/${encodeURIComponent(c.call_id)}/${pfad}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(body),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.grund || d.error || `HTTP ${r.status}`)
      if (d.schon && d.hinweis) toast(`Anruf ${d.hinweis}`, "warn")
      onChange()
      qc.invalidateQueries({ queryKey: ["ki-telefon-call", c.call_id] })
    } catch (e) {
      toast("Fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error")
    } finally {
      setBusy(null)
    }
  }

  // Rückruf: zum Abschnitt „Rückrufe" im Panel springen statt eines zweiten Dialogs.
  const zuRueckruf = () => {
    const el = document.querySelector('[data-testid="rueckruf-abschnitt"]')
    el?.scrollIntoView({ behavior: "smooth", block: "start" })
    ;(el?.querySelector("button") as HTMLButtonElement | null)?.focus({ preventScroll: true })
  }

  const knopf = "h-9 px-3.5 rounded-lg text-[13px] font-medium inline-flex items-center gap-2 transition-colors disabled:opacity-50"

  return (
    <div className="mt-4 space-y-2.5">
      <div className="flex flex-wrap items-center gap-2">
        {erledigt ? (
          <div className="h-9 pl-3 pr-1 rounded-lg bg-success/10 text-success text-[13px] font-medium inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            {(c.erledigt_von || "").startsWith("automatisch") ? c.erledigt_von : `Erledigt${c.erledigt_von ? ` · ${c.erledigt_von}` : ""}`}
            <button
              onClick={() => senden("erledigt", { erledigt: false })} disabled={!!busy}
              className="ml-1 h-7 px-2 rounded-md text-[12px] text-muted-foreground hover:text-foreground hover:bg-[var(--bg-card)] inline-flex items-center gap-1"
              title="Wieder öffnen"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Öffnen
            </button>
          </div>
        ) : (
          <button
            onClick={() => senden("erledigt", { erledigt: true })} disabled={!!busy}
            className={cn(knopf, "bg-success text-white hover:opacity-90")}
          >
            <Check className="w-4 h-4" /> {busy === "erledigt" ? "Speichert…" : "Als erledigt markieren"}
          </button>
        )}

        {/* Zuteilen: sieht aus wie ein Knopf, darunter liegt das native <select> */}
        <label className={cn(knopf, "relative border bg-[var(--bg-card)] hover:bg-secondary cursor-pointer",
          c.zugewiesen_an ? "text-foreground" : "text-muted-foreground")}>
          <UserRound className="w-4 h-4" />
          {c.zugewiesen_an || "Zuteilen"}
          <ChevronDown className="w-3.5 h-3.5 opacity-60" />
          <select
            value={c.zugewiesen_an || ""} disabled={!!busy}
            onChange={(e) => senden("zuweisen", { an: e.target.value })}
            className="absolute inset-0 opacity-0 cursor-pointer"
            aria-label="Anruf zuteilen"
          >
            <option value="">Niemand</option>
            {namen.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>

        <button onClick={zuRueckruf} className={cn(knopf, "border bg-[var(--bg-card)] hover:bg-secondary")}>
          <PhoneOutgoing className="w-4 h-4" /> Rückruf eintragen
        </button>
      </div>

      {/* #92: Termin des Bots prüfen — eigener Hinweis, nur solange nötig */}
      {termin && (termin.noetig ? (
        <div className="rounded-lg bg-amber-500/10 px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13px]">
          <CalendarCheck className="w-4 h-4 text-amber-700 dark:text-amber-400" />
          <span className="font-medium text-amber-800 dark:text-amber-300">Gebuchter Termin – bitte prüfen</span>
          <span className="text-[12.5px]">{terminLink(c)}</span>
          <button
            onClick={() => senden("termin-bestaetigt", { bestaetigt: true })} disabled={!!busy}
            className="ml-auto h-8 px-3 rounded-md bg-[var(--bg-card)] border text-[12.5px] font-medium inline-flex items-center gap-1.5 hover:bg-secondary"
          >
            <Check className="w-3.5 h-3.5" /> Termin passt
          </button>
        </div>
      ) : (
        <div className="text-[12.5px] text-muted-foreground flex items-center gap-2">
          <CalendarCheck className="w-3.5 h-3.5 text-success" />
          Termin geprüft{termin.von ? ` von ${termin.von}` : ""} {terminLink(c)}
          <button onClick={() => senden("termin-bestaetigt", { bestaetigt: false })} className="hover:text-foreground underline-offset-2 hover:underline">zurücknehmen</button>
        </div>
      ))}

    </div>
  )
}
